import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import talentLogo2 from "../assets/talentlogo2.png";
import usePageMeta from "../hooks/usePageMeta";
import { BASE_URL } from "../config";
import { loadManageContext, loadManageSelectedLocations, saveManageContext } from "./manageContext";
import { saveManageSession } from "./manageSession";
import { formatAppTime } from "../utils/appDateTime";

export default function ManagePassword() {
  usePageMeta("Sign In");
  const navigate = useNavigate();
  const location = useLocation();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const manageContext = useMemo(() => loadManageContext(location), [location]);
  const { email, clientName, accountId, contactRole, clientId, shiftId, timesheetId, clientDeviceId } =
    manageContext;

  useEffect(() => {
    saveManageContext(manageContext);
  }, [manageContext]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => formatAppTime(date);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!password.trim()) {
      setError("Password is required");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${BASE_URL}/auth/manage-signin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email,
          password,
          accountId,
          clientId,
          shiftId,
          timesheetId,
          clientDeviceId,
          browserDeviceGuid: manageContext?.browserDeviceGuid || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Invalid password");
      }
      const payload = await response.json();
      const nextClientId = payload?.client_id || manageContext.clientId || null;
      const nextClientDeviceId =
        payload?.clientDeviceId || payload?.client_device_id || manageContext.clientDeviceId || null;
      const storedSelectedLocationIds = loadManageSelectedLocations({
        clientId: nextClientId,
        clientDeviceId: nextClientDeviceId,
      });
      const sessionSelectedLocationIds =
        storedSelectedLocationIds.length > 0
          ? storedSelectedLocationIds
          : Array.isArray(manageContext.selectedLocationIds)
            ? manageContext.selectedLocationIds
            : [];
      console.info("[manage] sign-in success", {
        at: new Date().toISOString(),
        email,
        clientId: nextClientId,
        clientDeviceId: nextClientDeviceId,
      });
      saveManageSession({
        token: payload?.token,
        user: {
          accountId: payload?.account_id,
          clientId: payload?.client_id,
          email: payload?.email,
          capabilities: payload?.capabilities || null,
          roles: payload?.roles || [],
          permissions: payload?.permissions || [],
        },
      });
      const nextContext = {
        ...manageContext,
        capabilities: payload?.capabilities || manageContext.capabilities || null,
        roles: payload?.roles || manageContext.roles || [],
        permissions: payload?.permissions || manageContext.permissions || [],
        clientId: nextClientId,
        clientDeviceId: nextClientDeviceId,
        selectedLocationIds: sessionSelectedLocationIds,
        selectedLocationClientId: sessionSelectedLocationIds.length ? nextClientId : null,
        selectedLocationDeviceId: sessionSelectedLocationIds.length ? nextClientDeviceId : null,
      };
      saveManageContext(nextContext);
      
      // On success, navigate to dashboard
      const dashboardParams = new URLSearchParams({
        email,
        name: clientName,
      });
      if (accountId) dashboardParams.set("accountId", String(accountId));
      if (nextContext.clientId) {
        dashboardParams.set("clientId", String(nextContext.clientId));
      }
      if (contactRole) dashboardParams.set("contactRole", contactRole);
      if (nextContext.capabilities) {
        dashboardParams.set("capabilities", JSON.stringify(nextContext.capabilities));
      }
      if (nextContext.shiftId) dashboardParams.set("shiftId", String(nextContext.shiftId));
      if (nextContext.timesheetId) dashboardParams.set("timesheetId", String(nextContext.timesheetId));
      if (nextContext.locationName) dashboardParams.set("location", String(nextContext.locationName));
      if (nextContext.shiftDate) dashboardParams.set("shiftDate", String(nextContext.shiftDate));
      if (nextContext.startDate) dashboardParams.set("from", String(nextContext.startDate));
      if (nextContext.endDate) dashboardParams.set("to", String(nextContext.endDate));
      if (Array.isArray(nextContext.selectedLocationIds) && nextContext.selectedLocationIds.length) {
        dashboardParams.set("selectedLocationIds", nextContext.selectedLocationIds.join(","));
      }

      navigate(`/manage/dashboard?${dashboardParams.toString()}`, {
        state: { manageContext: nextContext },
      });
    } catch (err) {
      setError(err.message || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    const forgotParams = new URLSearchParams({ email });
    navigate(`/forgot-password?${forgotParams.toString()}`);
  };

  const handleLogoClick = () => {
    const urlParams = new URLSearchParams(location.search);
    if (!urlParams.has("clientId") && !urlParams.has("name") && !urlParams.has("client")) {
      navigate("/device");
      return;
    }
    const checkinParams = new URLSearchParams();
    if (clientId) checkinParams.set("clientId", String(clientId));
    if (manageContext.shiftDate) checkinParams.set("shiftDate", manageContext.shiftDate);
    if (manageContext.startDate) checkinParams.set("from", manageContext.startDate);
    if (manageContext.endDate) checkinParams.set("to", manageContext.endDate);
    if (clientName) checkinParams.set("client", clientName);
    if (manageContext.locationName) checkinParams.set("location", manageContext.locationName);
    
    const forceLoginVal = urlParams.get("forceLogin") || "1";
    checkinParams.set("forceLogin", forceLoginVal);

    navigate(`/checkin?${checkinParams.toString()}`);
  };

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      <div className="flex justify-between items-start p-4">
        <div className="cursor-pointer" onClick={handleLogoClick}>
          <img src={talentLogo2} alt="Talent Logo" className="h-12 w-auto" />
        </div>

        <div className="text-right">
          <div className="text-xl font-bold text-gray-800">{formatTime(currentTime)}</div>
          <div className="text-sm text-gray-600">{clientName}</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-10">
        <form onSubmit={handleSignIn} className="w-full max-w-sm">
          <h1 className="text-center text-3xl text-gray-700 mb-2">Sign In</h1>
          <p className="text-center text-sm text-gray-700 mb-8">{email}</p>

          <label htmlFor="manage-password" className="block text-sm text-gray-500 mb-1">
            Password*
          </label>

          <div className="relative mb-4">
            <input
              id="manage-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              className="w-full border-b border-gray-300 bg-transparent px-0 py-2 text-sm outline-none focus:border-gray-500 disabled:opacity-50"
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-500"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#DC1F00] text-white py-2 text-sm font-medium hover:bg-[#b91900] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "SIGNING IN..." : "SIGN IN"}
          </button>

          {error && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={handleForgotPassword}
            disabled={loading}
            className="w-full mt-2 text-center text-xs text-gray-700 hover:text-black disabled:opacity-50 disabled:cursor-not-allowed"
          >
            FORGOT PASSWORD
          </button>
        </form>
      </div>

      <div className="px-4 pb-4">
        <button
          type="button"
          onClick={() => {
            const signinParams = new URLSearchParams();
            if (email) signinParams.set("email", email);
            if (clientName) signinParams.set("name", clientName);
            if (accountId) signinParams.set("accountId", String(accountId));
            if (clientId) signinParams.set("clientId", String(clientId));
            if (contactRole) signinParams.set("contactRole", contactRole);
            if (manageContext.shiftId) signinParams.set("shiftId", String(manageContext.shiftId));
            if (manageContext.timesheetId) {
              signinParams.set("timesheetId", String(manageContext.timesheetId));
            }
            if (manageContext.locationName) {
              signinParams.set("location", String(manageContext.locationName));
            }
            if (manageContext.shiftDate) signinParams.set("shiftDate", String(manageContext.shiftDate));
            if (manageContext.startDate) signinParams.set("from", String(manageContext.startDate));
            if (manageContext.endDate) signinParams.set("to", String(manageContext.endDate));
            if (manageContext.clientDeviceId) {
              signinParams.set("clientDeviceId", String(manageContext.clientDeviceId));
            }
            if (Array.isArray(manageContext.selectedLocationIds) && manageContext.selectedLocationIds.length) {
              signinParams.set("selectedLocationIds", manageContext.selectedLocationIds.join(","));
            }
            const qs = signinParams.toString();
            navigate(qs ? `/manage/signin?${qs}` : "/manage/signin");
          }}
          className="w-full bg-red-50 text-red-400 py-2 text-sm font-medium hover:bg-red-100 transition-colors"
        >
          CANCEL
        </button>
      </div>
    </div>
  );
}
